import React from 'react';
import { useQuery } from '@apollo/client';
import { DocumentIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { GET_USER_INVOICES } from '@shared/graphql/queries/subscriptionPackages';

interface Invoice {
  id: string;
  userId: string;
  stripeInvoiceId: string;
  stripeCustomerId: string;
  stripeSubscriptionId?: string;
  amountPaid: number;
  amountDue: number;
  amountRemaining: number;
  subtotal: number;
  total: number;
  currency: string;
  status: string;
  paidAt?: string;
  billingReason?: string;
  createdDate: string;
  invoicePdfUrl?: string;
  hostedInvoiceUrl?: string;
  invoiceNumber?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  gstAmount?: number;
  netAmount?: number;
  packageName?: string;
  billingCycle?: string;
}

const InvoiceAccess: React.FC = () => {
  const { data, loading, error } = useQuery(GET_USER_INVOICES, {
    fetchPolicy: 'cache-and-network'
  });

  const invoices: Invoice[] = data?.getUserInvoices || [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusColors[status as keyof typeof statusColors] || statusColors.draft
      }`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleDownload = (url: string, filename: string) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading invoices...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-sm text-red-700">
          Failed to load invoices. Please try again later.
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-8">
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices available</h3>
        <p className="mt-1 text-sm text-gray-500">
          Invoices will appear here for your subscriptions
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      <div className="space-y-3">
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {invoice.invoiceNumber}
                  </h4>
                  {getStatusBadge(invoice.status)}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Package:</span> {invoice.packageName}
                  </div>
                  <div>
                    <span className="font-medium">Date:</span> {formatDate(invoice.createdDate)}
                  </div>
                  {invoice.billingCycle && (
                    <div>
                      <span className="font-medium">Billing:</span> {invoice.billingCycle}
                    </div>
                  )}
                  {invoice.paidAt && (
                    <div>
                      <span className="font-medium">Paid:</span> {formatDate(invoice.paidAt)}
                    </div>
                  )}
                </div>

                {/* GST Breakdown for all invoices */}
                {invoice.gstAmount && invoice.netAmount && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      GST Breakdown
                    </h5>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Net Amount:</div>
                        <div className="font-medium">{formatCurrency(invoice.netAmount)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">GST (10%):</div>
                        <div className="font-medium">{formatCurrency(invoice.gstAmount)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Total (inc GST):</div>
                        <div className="font-semibold text-green-600">{formatCurrency(invoice.total)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col space-y-2 ml-4">
                {invoice.invoicePdfUrl && (
                  <button
                    onClick={() => handleDownload(invoice.invoicePdfUrl!, `${invoice.invoiceNumber}.pdf`)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <DocumentIcon className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                )}
                {invoice.hostedInvoiceUrl && (
                  <button
                    onClick={() => handleDownload(invoice.hostedInvoiceUrl!, 'invoice')}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                    View Online
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <div className="text-xs text-gray-500">
          <p className="mb-1">
            <strong>Business Details:</strong> TippingIQ | ABN: 50 635 662 529 |
            Address: 57 Shelley St, Cannon Hill, 4170 | Phone: +61437946891
          </p>
          <p>
            These invoices comply with Australian GST requirements.
            Please retain for your records and tax purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceAccess;